import apiClient from "./apiClient";
import axios from "axios";
import { User } from "@quantizr-engineering/common/types/user";
import { useState } from "react";
import { toast } from "sonner";
import { useSetAtom } from "jotai";
import { runsAtom } from "../atoms/runAtoms";
import { showsAtom } from "../atoms/showAtoms";
import { recordedAtom } from "../atoms/recordedAtoms";
import { releasesAtom } from "../atoms/releaseAtoms";
import { useNavigate } from "react-router-dom";
import { searchResultsAtom } from "../atoms/searchAtom";
import { fileEntitiesAtom } from "../atoms/fileAtoms";
import { userAtom } from "../atoms/userAtoms";
import { teamMembersAtom, teamsForMemberAtom } from "../atoms/teamAtoms";
import posthog from "posthog-js";
import { parseJwt } from "../pages/Dashboard/Dashboard";

const resetValue = {
  loading: false,
  data: null,
  lastFetched: null,
};

const postHogIdentify = async (email: string, user: any) => {
  const uniqueId = email; // TOOD: This might need to be a combo of team+email or something eventually
  posthog.identify(
    uniqueId,
    {
      email: email,
      username: user.username || '',
      teamID: user.teamID || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      teamName: user.teamName || '',
    },
  );
};

export const useAuthService = () => {
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);
  const resetRuns = useSetAtom(runsAtom);
  const resetShows = useSetAtom(showsAtom);
  const resetRecordings = useSetAtom(recordedAtom);
  const resetReleases = useSetAtom(releasesAtom);
  const resetSearch = useSetAtom({ ...searchResultsAtom, query: "" });
  const resetFiles = useSetAtom(fileEntitiesAtom);
  const resetUser = useSetAtom(userAtom);
  const resetTeam = useSetAtom(teamMembersAtom);
  const resetTeamsForMember = useSetAtom(teamsForMemberAtom);

  const navigate = useNavigate();

  const login = async (email: string, password: string) => {
    const apiUrl = import.meta.env.VITE_QZ_MONOLITH_URL;
    try {
      setIsLoginLoading(true);
      const response = await axios.post(`${apiUrl}/login`, { email, password });
      if (response.data.access_token) {
        const user = parseJwt(response.data.access_token || "");
        postHogIdentify(email, user);
        localStorage.setItem("authToken", response.data.access_token);
      }
      if (response.data.refresh_token) {
        localStorage.setItem("refreshToken", response.data.refresh_token);
      }
      return response;
    } catch (error) {
      toast.error("Incorrect username or password");
      throw error;
    } finally {
      setIsLoginLoading(false);
    }
  };

  const switchTeam = async (teamID: number) => {
    try {
      const response = await apiClient.post("/auth/team/switch", { teamID });
      if (response.data.access_token) {
        const user = parseJwt(response.data.access_token || "");
        postHogIdentify(user.username, user);
        localStorage.setItem("authToken", response.data.access_token);
      }
      if (response.data.refresh_token) {
        localStorage.setItem("refreshToken", response.data.refresh_token);
      }

      const currentPath = window.location.pathname;
      const pathSegments = currentPath.split('/').filter(Boolean);
      if (pathSegments.length > 1 && !['manager', 'analytics'].includes(pathSegments[0])) {
        window.location.href = `${window.location.origin}/${pathSegments[0]}`;
      } else {
        window.location.reload();
      }
      return response;
    } catch (error) {
      toast.error("Unable to switch teams");
      throw error;
    }
  };

  const logout = () => {
    navigate("/login");
    resetRecordings(resetValue);
    resetReleases(resetValue);
    resetRuns(resetValue);
    resetShows(resetValue);
    resetSearch({ ...resetValue, query: "" });
    resetFiles(resetValue);
    resetUser({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      teamName: "",
      userRole: [],
      teamMembers: [],
      numberOfMembers: "",
    });
    resetTeam(resetValue);
    resetTeamsForMember(resetValue);
    posthog.reset()
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
  };

  const register = async (userInfo: User) => {
    try {
      setIsRegisterLoading(true);
      // Convert UserRole[] to string[]
      const userRoleAsString =
        userInfo.userRole.length > 0 ? [userInfo.userRole[0]] : [];

      const response = await apiClient.post("/user/register", {
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
        acceptTermsOfService: true,
        password: userInfo.password,
        teamName: userInfo.teamName,
        userRoles: userRoleAsString,
        invitedUsers: userInfo.teamMembers?.map((user) => ({
          ...user,
          userRoles:
            user.userRole && user.userRole.length > 0 ? [user.userRole[0]] : [],
        })),
      });
      toast.success("Sucessfuly registered");
      if (response.data.access_token) {
        const user = parseJwt(response.data.access_token || "");
        postHogIdentify(userInfo.email, user);
        localStorage.setItem("authToken", response.data.access_token);
        console.log('aw yeah')
      }
      if (response.data.refresh_token) {
        localStorage.setItem("refreshToken", response.data.refresh_token);
      }
      return response;
    } catch (error) {
      toast.error("Unable to register");
      throw error;
    } finally {
      setIsRegisterLoading(false);
    }
  };

  const registerInvitedUser = async (userInfo: {
    password: string;
    email: string;
    accessKey: string;
    firstName: string;
    lastName: string;
  }) => {
    try {
      setIsRegisterLoading(true);
      const response = await apiClient.post("/user/register/team", {
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
        password: userInfo.password,
        accessKey: userInfo.accessKey,
      });
      toast.success("Sucessfuly registered");
      return response;
    } catch (error) {
      toast.error("Unable to register");
      throw error;
    } finally {
      setIsRegisterLoading(false);
    }
  };

  const findInvitedUser = async (accessKey: string) => {
    try {
      const response = await apiClient.get(`/user/invited/${accessKey}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const initPasswordReset = async (email: string) => {
    try {
      const response = await apiClient.post(
       
        "/user/password-reset-init",
        {
          email: email,
        }
      );
      toast.success("Password reset request email sent successfully");
      return response.data;
    } catch (err) {
      toast.error("Password reset request email failed");
      throw err;
    } 
  }

  const passwordReset = async (resetInfo: {email: string, accessKey: string, password: string}) => {
    try {
      const response = await apiClient.post("/user/password-reset-finish", {
        email: resetInfo.email,
        accessKey: resetInfo.accessKey,
        password: resetInfo.password,
      });
      toast.success("Password reset successful");
      return response.data;
    } catch (error) {
      toast.error("Password reset failed");
      throw error;
    }
  };

  return {
    isLoginLoading,
    isRegisterLoading,
    login,
    register,
    findInvitedUser,
    registerInvitedUser,
    initPasswordReset,
    passwordReset,
    logout,
    switchTeam,
  };
};
